import {Component, EventEmitter, Input, Output} from '@angular/core';
import {CommonModule} from '@angular/common';

@Component({
  selector: 'ui-input',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ui-input.component.html',
  styleUrl: './ui-input.component.scss'
})
export class UiInputComponent {
  @Input() type: 'text' | 'search' | 'email' | 'password' | 'number' = 'text';
  @Input() placeholder = '';
  @Input() value = '';
  @Input() disabled = false;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() fullWidth = false;
  @Input() ariaLabel?: string;
  @Input() className = '';

  /**
   * Optional DaisyUI validator hint message displayed below the input.
   *
   * Intended to be used with the `validator` class on the input.
   */
  @Input() hint?: string;

  /**
   * Whether to show the hint.
   */
  @Input() showHint = false;

  /**
   * Show a clear button when there's a value
   */
  @Input() clearable = false;

  /**
   * DaisyUI's `validator-hint` visibility is driven by native HTML validation states
   * (e.g. `:invalid`). Angular form validation alone doesn't affect those.
   *
   * When true, we intentionally make the input fail native validation so that
   * DaisyUI can show the hint.
   */
  @Input() forceInvalid = false;

  @Output() valueChange = new EventEmitter<string>();
  @Output() cleared = new EventEmitter<void>();
  @Output() focused = new EventEmitter<void>();
  @Output() blurred = new EventEmitter<void>();

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value = target.value;
    this.valueChange.emit(this.value);
  }

  onClear(): void {
    this.value = '';
    this.valueChange.emit(this.value);
    this.cleared.emit();
  }

  onFocus(): void {
    this.focused.emit();
  }

  onBlur(): void {
    this.blurred.emit();
  }

  get computedClasses(): string {
    const classes = ['input', 'input-bordered', 'text-base', 'md:text-sm'];

    if (this.size === 'sm') {
      classes.push('input-sm');
    } else if (this.size === 'lg') {
      classes.push('input-lg');
    }

    if (this.fullWidth) {
      classes.push('w-full');
    }

    return [...classes, this.className].join(' ').trim();
  }

  get showClearButton(): boolean {
    return this.clearable && !!this.value && !this.disabled;
  }

  get shouldReserveHintSpace(): boolean {
    return this.showHint && !!this.hint;
  }
}
